import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ComposableMap, Geographies, Geography, Marker, Line } from "react-simple-maps";
import { Globe } from "lucide-react";
import { MARKETS } from "../data/content";
import { Reveal, Eyebrow } from "./Reveal";

const GEO_URL = "/geo/world-110m.json";
const HUB = MARKETS[0].coordinates; // India

export const Markets = () => {
  const [hovered, setHovered] = useState(null);
  const [auto, setAuto] = useState(1);
  const paused = hovered !== null;

  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => {
      setAuto((a) => (a + 1) % MARKETS.length);
    }, 2600);
    return () => clearInterval(id);
  }, [paused]);

  const activeIndex = hovered !== null ? hovered : auto;
  const active = MARKETS[activeIndex];

  return (
    <section data-testid="markets-section" id="markets" className="relative py-28 lg:py-40 overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[420px] w-[700px] rounded-full bg-gold/5 blur-[150px] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* Left copy */}
          <div className="lg:col-span-4">
            <Reveal>
              <Eyebrow testid="markets-eyebrow">Global Footprint</Eyebrow>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-6 leading-[1.02]">
                Growth across <span className="italic text-gold-gradient">12+ markets.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="text-cream/55 mt-6 text-base sm:text-lg leading-relaxed">
                From D2C brands in the US and UK to consumer majors across the UAE, Southeast
                Asia and South Africa — hover the map to see where I've built growth engines.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="mt-8 flex items-center gap-3 text-gold">
                <Globe size={20} />
                <span className="font-serif text-xl sm:text-2xl">India · US · EU · UK · UAE · APAC</span>
              </div>
            </Reveal>

            <Reveal delay={0.32}>
              <div
                data-testid="market-proof"
                className="mt-8 min-h-[92px] rounded-2xl border border-gold/20 bg-charcoal-800/40 px-6 py-5 transition-colors"
              >
                <div>
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="font-serif text-2xl text-cream">{active.name}</span>
                    <span className="font-serif text-2xl text-gold-gradient">{active.stat}</span>
                  </div>
                  <p className="text-cream/55 text-sm mt-1.5">{active.note}</p>
                  <p className="text-gold/50 text-[10px] uppercase tracking-[0.25em] mt-3">
                    {paused ? "Exploring" : "Auto-touring · hover to explore"}
                  </p>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.35}>
              <div className="mt-8 flex flex-wrap gap-2">
                {MARKETS.map((m, i) => (
                  <button
                    key={m.name}
                    data-testid={`market-chip-${i}`}
                    onMouseEnter={() => setHovered(i)}
                    onMouseLeave={() => setHovered(null)}
                    className={`px-3 py-1.5 rounded-full border text-xs transition-colors ${
                      activeIndex === i
                        ? "border-gold text-gold bg-gold/10"
                        : "border-white/10 text-cream/60 hover:border-gold/40"
                    }`}
                  >
                    {m.name}
                  </button>
                ))}
              </div>
            </Reveal>
          </div>

          {/* Map */}
          <Reveal delay={0.2} className="lg:col-span-8">
            <div
              data-testid="world-map"
              className="relative rounded-3xl border border-white/10 bg-charcoal-800/30 overflow-hidden"
            >
              <ComposableMap
                projection="geoEqualEarth"
                projectionConfig={{ scale: 165 }}
                style={{ width: "100%", height: "auto" }}
              >
                <Geographies geography={GEO_URL}>
                  {({ geographies }) =>
                    geographies.map((geo) => (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        style={{
                          default: { fill: "#1c1c20", stroke: "#0A0A0B", strokeWidth: 0.5, outline: "none" },
                          hover: { fill: "#26262b", outline: "none" },
                          pressed: { fill: "#26262b", outline: "none" },
                        }}
                      />
                    ))
                  }
                </Geographies>

                {/* Animated glowing arcs from India to each market */}
                {MARKETS.map((m, i) => {
                  if (m.hub) return null;
                  const hubActive = activeIndex === 0;
                  const isActive = activeIndex === i;
                  return (
                    <Line
                      key={`route-${i}`}
                      data-testid={`route-${i}`}
                      from={HUB}
                      to={m.coordinates}
                      stroke="#D4AF37"
                      strokeWidth={isActive ? 1.6 : 0.9}
                      strokeLinecap="round"
                      className="route-line"
                      style={{ opacity: hubActive ? 0.4 : isActive ? 0.9 : 0.12 }}
                    />
                  );
                })}

                {MARKETS.map((m, i) => {
                  const isActive = activeIndex === i;
                  const label = `${m.name} · ${m.stat}`;
                  return (
                    <Marker
                      key={m.name}
                      coordinates={m.coordinates}
                      onMouseEnter={() => setHovered(i)}
                      onMouseLeave={() => setHovered(null)}
                      data-testid={`map-pin-${i}`}
                    >
                      <g className="cursor-pointer">
                        {/* glow halo */}
                        <circle r={isActive ? 16 : m.hub ? 12 : 11} fill="#D4AF37" opacity={isActive ? 0.25 : 0.14}>
                          <animate attributeName="r" values={`${isActive ? 10 : 7};${isActive ? 18 : 13};${isActive ? 10 : 7}`} dur="2.4s" repeatCount="indefinite" />
                          <animate attributeName="opacity" values="0.28;0.05;0.28" dur="2.4s" repeatCount="indefinite" />
                        </circle>
                        {/* core pin */}
                        <circle r={isActive ? 4.8 : m.hub ? 4.2 : 3.2} fill={m.hub ? "#D4AF37" : "#E8CF8A"} stroke="#0A0A0B" strokeWidth={0.6} />
                        {isActive && (
                          <g pointerEvents="none">
                            <rect x={9} y={-22} width={label.length * 5.6 + 16} height={22} rx={6} fill="#0A0A0B" stroke="#D4AF37" strokeWidth={0.6} />
                            <text x={17} y={-7} fill="#F8F5EE" fontSize={10} fontFamily="Outfit, sans-serif">
                              <tspan fill="#F8F5EE">{m.name} · </tspan>
                              <tspan fill="#E8CF8A">{m.stat}</tspan>
                            </text>
                          </g>
                        )}
                      </g>
                    </Marker>
                  );
                })}
              </ComposableMap>
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-charcoal-900/40 via-transparent to-transparent" />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
